import React from 'react';
import { aboutContent } from '../data/trekData';
import { Shield, Users, Mountain, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const About = () => {
  const valueIcons = {
    'Safety First': Shield,
    'Expert Guidance': Users,
    'Sustainable Tourism': Mountain,
    'Quality Equipment': Award
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-slate-900/70 z-10" />
          <img
            src="https://images.unsplash.com/photo-1519904981063-b0cf448d479e?w=1920&q=80"
            alt="Mountain team"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-20 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            About Us
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Passionate about mountains, dedicated to your adventure
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">{aboutContent.title}</h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              {aboutContent.description}
            </p>
            <p className="text-lg text-gray-700 mb-12 leading-relaxed">
              {aboutContent.mission}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-600 mb-2">10+</div>
                <div className="text-gray-600">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-600 mb-2">50+</div>
                <div className="text-gray-600">Treks Organized</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-600 mb-2">5000+</div>
                <div className="text-gray-600">Happy Trekkers</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-600 mb-2">15+</div>
                <div className="text-gray-600">Expert Guides</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {aboutContent.values.map((value, index) => {
              const IconComponent = valueIcons[value.title];
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardHeader>
                    <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="h-7 w-7 text-orange-600" />
                    </div>
                    <CardTitle className="text-2xl">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Meet Our Team</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experienced mountaineers and certified guides ready to lead your adventure
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-50 rounded-lg p-8">
              <p className="text-lg text-gray-700 leading-relaxed text-center">
                Our team consists of certified mountain guides with extensive experience in high-altitude trekking,
                wilderness first aid, and mountain rescue operations. Each guide brings unique expertise and a
                deep passion for the Himalayas, ensuring your trek is not only safe but also enriching and
                educational. We maintain the highest standards of professionalism and are committed to creating
                memorable experiences for every trekker.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;